import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除。</p>
            <Link to="/" className="btn-primary text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = getRelatedComics(comic, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 妖精漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    url: `https://yaojingmanhua.com/comic/${comic.id}`,
    publisher: {
      '@type': 'Organization',
      name: '妖精漫画',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: comic.category, to: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 mt-4">
            <div className="w-full lg:w-[300px] flex-shrink-0">
              <div className="rounded-lg overflow-hidden aspect-[3/4] bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - 妖精漫画在线阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">
                    {tag}
                  </span>
                ))}
                <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                  comic.status === '已完结'
                    ? 'bg-secondary-100 text-secondary-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2 font-heading">
                {comic.title}
              </h1>

              <p className="text-sm text-foreground-500 mb-4">
                作者：<strong className="text-foreground-800">{comic.author}</strong>
                <span className="mx-2">·</span>
                类型：{comic.type}
                <span className="mx-2">·</span>
                更新于 {comic.updatedAt}
              </p>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">分</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-500 text-sm">
                  <i className="ri-eye-line"></i>
                  {(comic.views / 10000).toFixed(1)}万阅读
                </div>
                <div className="flex items-center gap-1 text-foreground-500 text-sm">
                  <i className="ri-heart-line"></i>
                  {(comic.favorites / 10000).toFixed(1)}万收藏
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-foreground-900 mb-2 font-heading">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex items-center gap-3">
                <button className="btn-primary text-sm px-8 py-3 cursor-pointer">
                  <i className="ri-play-circle-line mr-2"></i>
                  开始阅读
                </button>
                <button className="btn-outline text-sm px-6 py-3 cursor-pointer">
                  <i className="ri-bookmark-line mr-2"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">章节列表</h2>
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="divide-y divide-background-200/70">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-500 font-mono">第{chapter.number}话</span>
                      <span className="text-sm text-foreground-800">{chapter.title}</span>
                    </div>
                    <span className="text-xs text-foreground-500">{chapter.updatedAt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-12">
              <h2 className="section-title mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}